import React, { useState, useEffect, useCallback } from 'react';
import { clientAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import {
  FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiMapPin,
  FiPhone, FiBriefcase, FiRefreshCw, FiSearch, FiX, FiUserPlus
} from 'react-icons/fi';

const CURRENCIES = [
  { value:'USD', label:'USD ($) — US Dollar' },
  { value:'INR', label:'INR (₹) — Indian Rupee' },
  { value:'EUR', label:'EUR (€) — Euro' },
  { value:'GBP', label:'GBP (£) — British Pound' },
  { value:'AED', label:'AED (د.إ) — UAE Dirham' },
  { value:'CAD', label:'CAD (C$) — Canadian Dollar' },
  { value:'AUD', label:'AUD (A$) — Australian Dollar' },
  { value:'SGD', label:'SGD (S$) — Singapore Dollar' },
  { value:'OTHER', label:'Other' },
];
const currencyLabel = (v) => CURRENCIES.find(c=>c.value===v)?.label || v;

const CLIENT_TYPES = [
  { value:'direct_india_no_gst', label:'Direct Indian Client (No GST)',   icon:'🇮🇳' },
  { value:'direct_india_gst',    label:'Direct Indian Client (With GST)', icon:'🧾' },
  { value:'upwork_personal',     label:'Upwork Personal Account',         icon:'💼' },
  { value:'upwork_company',      label:'Upwork Company Account',          icon:'🏢' },
  { value:'wise',                label:'Wise Client',                     icon:'💳' },
  { value:'paypal',              label:'PayPal Client',                   icon:'🅿️' },
  { value:'referral',            label:'Referral (Friend/Partner)',        icon:'🤝' },
  { value:'other',               label:'Other',                           icon:'🔖' },
];
const clientTypeLabel = (v) => CLIENT_TYPES.find(t=>t.value===v)?.label || v;
const clientTypeIcon  = (v) => CLIENT_TYPES.find(t=>t.value===v)?.icon  || '🔖';

const NOTE_TYPES = ['general','call','meeting','email','follow_up','whatsapp','requirement'];
const NOTE_CFG = {
  general:     { bg:'#f1f5f9', text:'#475569', icon:'📝', label:'Note' },
  call:        { bg:'#dbeafe', text:'#1e40af', icon:'📞', label:'Call' },
  meeting:     { bg:'#dcfce7', text:'#14532d', icon:'🤝', label:'Meeting' },
  email:       { bg:'#fef3c7', text:'#92400e', icon:'📧', label:'Email' },
  follow_up:   { bg:'#fce7f3', text:'#831843', icon:'🔔', label:'Follow-up' },
  whatsapp:    { bg:'#dcfce7', text:'#15803d', icon:'💬', label:'WhatsApp' },
  requirement: { bg:'#ede9fe', text:'#5b21b6', icon:'📋', label:'Requirement' },
};
const PROJ_STATUS = {
  active:     { bg:'#dcfce7', text:'#14532d', label:'Active' },
  in_progress:{ bg:'#dbeafe', text:'#1e40af', label:'In Progress' },
  completed:  { bg:'#f1f5f9', text:'#475569', label:'Completed' },
  on_hold:    { bg:'#fef3c7', text:'#92400e', label:'On Hold' },
  cancelled:  { bg:'#fee2e2', text:'#991b1b', label:'Cancelled' },
};
const INV_COLOR = { draft:'#94a3b8', sent:'var(--pm-primary)', paid:'#22c55e', overdue:'#ef4444', cancelled:'#94a3b8' };

const INP = {
  width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10,
  fontSize:14, outline:'none', boxSizing:'border-box', background:'#fafbfc', fontFamily:"'DM Sans',sans-serif"
};
const fmt = (n) => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const ava = (name='?', sz=40) => ({
  width:sz, height:sz, borderRadius:'50%', background:'var(--pm-gradient)',
  display:'flex', alignItems:'center', justifyContent:'center', color:'#fff',
  fontSize: sz/2.6, fontWeight:700, flexShrink:0, textTransform:'uppercase', userSelect:'none'
});

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();

  const [clients,       setClients]       = useState([]);
  const [stats,         setStats]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [selected,      setSelected]      = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab,     setActiveTab]     = useState('notes');

  const [noteModal,  setNoteModal]  = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [noteForm,   setNoteForm]   = useState({ content:'', type:'general', isPinned:false, title:'', followUpDate:'', projectId:'' });
  const [savingNote, setSavingNote] = useState(false);

  const [editModal,    setEditModal]    = useState(false);
  const [clientForm,   setClientForm]   = useState({});
  const [savingClient, setSavingClient] = useState(false);

  // ── Onboard new client ──────────────────────────────────────────────────────
  const [onboardModal, setOnboardModal] = useState(false);
  const [onboardForm,  setOnboardForm]  = useState({ name:'', email:'', password:'', company:'', phone:'', clientType:'', transactionCurrency:'USD' });
  const [onboardSaving,setOnboardSaving]= useState(false);

  const handleOnboard = async (e) => {
    e.preventDefault();
    if (!onboardForm.name || !onboardForm.email || !onboardForm.password) return toast.error('Name, email and password are required');
    setOnboardSaving(true);
    try {
      await userAPI.create({ ...onboardForm, role: 'client' });
      toast.success(`Client "${onboardForm.name}" onboarded! They can now log in.`);
      setOnboardModal(false);
      setOnboardForm({ name:'', email:'', password:'', company:'', phone:'' });
      loadList();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to onboard client'); }
    setOnboardSaving(false);
  };

  // ── List ────────────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, sr] = await Promise.all([clientAPI.getAll({ search }), clientAPI.getStats()]);
      setClients(cr.data.clients || []);
      setStats(sr.data);
    } catch { toast.error('Failed to load clients'); }
    setLoading(false);
  }, [search]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Detail ──────────────────────────────────────────────────────────────────
  const openDetail = async (c) => {
    const id = c._id || c.id;
    setLoadingDetail(true);
    setActiveTab('notes');
    setSelected({ ...c, _id: id, projects:[], invoices:[], notes:[], stats:{} });
    try {
      const r = await clientAPI.getOne(id);
      setSelected(prev => ({
        ...prev, ...(r.data.client||{}), _id: id,
        projects: r.data.projects||[], invoices: r.data.invoices||[],
        notes: r.data.notes||[], stats: r.data.stats||{},
      }));
    } catch { toast.error('Failed to load client details'); }
    setLoadingDetail(false);
  };

  const refreshDetail = async () => {
    if (!selected) return;
    try {
      const r = await clientAPI.getOne(selected._id);
      setSelected(prev => ({
        ...prev, ...(r.data.client||{}), _id: prev._id,
        projects: r.data.projects||[], invoices: r.data.invoices||[],
        notes: r.data.notes||[], stats: r.data.stats||{},
      }));
    } catch {}
  };

  // ── Notes ───────────────────────────────────────────────────────────────────
  const openAddNote  = () => { setEditNoteId(null); setNoteForm({ content:'', type:'general', isPinned:false, title:'', followUpDate:'', projectId:'' }); setNoteModal(true); };
  const openEditNote = (n) => { setEditNoteId(n._id||n.id); setNoteForm({ content:n.content, type:n.type||'general', isPinned:!!n.isPinned, title:n.title||'', followUpDate:n.followUpDate||'', projectId:n.projectId||'' }); setNoteModal(true); };

  const handleSaveNote = async (e) => {
    e.preventDefault(); setSavingNote(true);
    try {
      if (editNoteId) { await clientAPI.updateNote(selected._id, editNoteId, noteForm); toast.success('Note updated'); }
      else            { await clientAPI.createNote(selected._id, noteForm);             toast.success('Note added!'); }
      setNoteModal(false); refreshDetail();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    const ok = await confirm('Delete this note?', { title: 'Delete Note?', variant: 'danger' });
    if (!ok) return;
    try { await clientAPI.deleteNote(selected._id, noteId); toast.success('Deleted'); refreshDetail(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handlePinNote = async (n) => {
    try { await clientAPI.updateNote(selected._id, n._id||n.id, { isPinned: !n.isPinned }); refreshDetail(); }
    catch {}
  };

  // ── Delete client ────────────────────────────────────────────────────────────
  const handleDeleteClient = async (e, c) => {
    e.stopPropagation();
    const ok = await confirm('This will permanently delete the client account and all their records.', { title: 'Delete Client?', variant: 'danger' });
    if (!ok) return;
    try {
      await userAPI.delete(c._id || c.id);
      toast.success('Client deleted');
      loadList();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  // ── Edit client ─────────────────────────────────────────────────────────────
  const openEditClient = () => {
    setClientForm({ name:selected.name||'', phone:selected.phone||'', company:selected.company||'', position:selected.position||'', isActive:selected.isActive!==false, clientType:selected.clientType||'', transactionCurrency:selected.transactionCurrency||'USD' });
    setEditModal(true);
  };
  const handleSaveClient = async (e) => {
    e.preventDefault(); setSavingClient(true);
    try {
      await clientAPI.updateClient(selected._id, clientForm);
      toast.success('Client updated'); setEditModal(false); refreshDetail(); loadList();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSavingClient(false);
  };

  // ── RENDER: list view ───────────────────────────────────────────────────────
  if (!selected) return (
    <div style={{ padding:'28px 32px', background:'#f8fafc', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:700, color:'#1e293b' }}>Clients</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:14 }}>CRM — manage client relationships & history</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={loadList} style={{ padding:'9px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', color:'#475569' }}>
            <FiRefreshCw size={15}/>
          </button>
          {isAdmin && (
            <button onClick={() => setOnboardModal(true)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 18px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 }}>
              <FiUserPlus size={15}/> Onboard Client
            </button>
          )}
        </div>
      </div>

      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          {[
            { label:'Total Clients',  value: stats.total,        icon:'👥', color:'var(--pm-primary)', bg:'#eef2ff' },
            { label:'Active',         value: stats.active,       icon:'✅', color:'#22c55e', bg:'#dcfce7' },
            { label:'Projects',       value: stats.projects,     icon:'📁', color:'#0ea5e9', bg:'#e0f2fe' },
            { label:'Total Revenue',  value: fmt(stats.revenue), icon:'💰', color:'#f59e0b', bg:'#fef3c7' },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius:14, padding:'18px 20px', boxShadow:'0 1px 4px #0000000d', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ position:'relative', marginBottom:20, maxWidth:420 }}>
        <FiSearch style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} size={15}/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, email or company…" style={{ ...INP, paddingLeft:38 }}/>
        {search && <button onClick={()=>setSearch('')} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }}><FiX size={14}/></button>}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:64, color:'#94a3b8', fontSize:15 }}>Loading clients…</div>
      ) : clients.length === 0 ? (
        <div style={{ textAlign:'center', padding:64, color:'#94a3b8' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>👥</div>
          <div style={{ fontWeight:600, fontSize:16, marginBottom:6 }}>No clients yet</div>
          <div style={{ fontSize:13, marginBottom:16 }}>Click "Onboard Client" to give a client their login credentials.</div>
          {isAdmin && <button onClick={() => setOnboardModal(true)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 }}><FiUserPlus size={14}/> Onboard Client</button>}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
          {clients.map(c => (
            <div key={c._id||c.id} onClick={()=>openDetail(c)}
              style={{ background:'#fff', borderRadius:16, padding:20, boxShadow:'0 1px 4px #0000000d', cursor:'pointer', transition:'all .2s', border:'1.5px solid transparent' }}
              onMouseEnter={ev=>{ev.currentTarget.style.transform='translateY(-2px)';ev.currentTarget.style.boxShadow='0 8px 24px #0000001a';ev.currentTarget.style.borderColor='var(--pm-primary)'}}
              onMouseLeave={ev=>{ev.currentTarget.style.transform='';ev.currentTarget.style.boxShadow='0 1px 4px #0000000d';ev.currentTarget.style.borderColor='transparent'}}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                <div style={ava(c.name, 44)}>{(c.name||'C').charAt(0)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, color:'#1e293b', fontSize:15, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</div>
                  <div style={{ fontSize:12, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.email}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ background:c.isActive!==false?'#dcfce7':'#f1f5f9', color:c.isActive!==false?'#14532d':'#94a3b8', padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
                    {c.isActive!==false?'Active':'Inactive'}
                  </span>
                  {isAdmin && (
                    <button onClick={(e)=>handleDeleteClient(e,c)} title="Delete client"
                      style={{ padding:'4px 7px', borderRadius:7, border:'none', background:'#fee2e2', color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', flexShrink:0 }}>
                      <FiTrash2 size={12}/>
                    </button>
                  )}
                </div>
              </div>
              {(c.company||c.phone) && (
                <div style={{ fontSize:13, color:'#64748b', marginBottom:10, display:'flex', flexDirection:'column', gap:4 }}>
                  {c.company && <span><FiBriefcase size={12} style={{marginRight:5,verticalAlign:'middle'}}/>{c.company}</span>}
                  {c.phone   && <span><FiPhone size={12} style={{marginRight:5,verticalAlign:'middle'}}/>{c.phone}</span>}
                </div>
              )}
              {c.clientType && (
                <div style={{ marginBottom:10 }}>
                  <span style={{ background:'#f0f0ff', color:'var(--pm-primary)', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>
                    {clientTypeIcon(c.clientType)} {clientTypeLabel(c.clientType)}
                  </span>
                </div>
              )}
              <div style={{ display:'flex', gap:24, paddingTop:10, borderTop:'1px solid #f1f5f9' }}>
                <div>
                  <div style={{ fontSize:18, fontWeight:700, color:'var(--pm-primary)' }}>{c.projectCount||0}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>Projects</div>
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#22c55e' }}>{fmt(c.revenue)}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>Revenue</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Onboard Client Modal ──────────────────────────────────────────────── */}
      {onboardModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:480, boxShadow:'0 20px 60px #0000002a' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <h3 style={{ margin:0, fontSize:18, fontWeight:700 }}>Onboard New Client</h3>
                <p style={{ margin:'4px 0 0', fontSize:13, color:'#64748b' }}>Creates a login account so the client can access their projects & invoices.</p>
              </div>
              <button onClick={() => setOnboardModal(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#94a3b8' }}><FiX/></button>
            </div>
            <form onSubmit={handleOnboard} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Full Name *</label>
                <input style={INP} value={onboardForm.name} onChange={e=>setOnboardForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Rahul Sharma" required />
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Email *</label>
                <input style={INP} type="email" value={onboardForm.email} onChange={e=>setOnboardForm(f=>({...f,email:e.target.value}))} placeholder="rahul@company.com" required />
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Password *</label>
                <input style={INP} type="password" value={onboardForm.password} onChange={e=>setOnboardForm(f=>({...f,password:e.target.value}))} placeholder="Share this with the client" required />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Company</label>
                  <input style={INP} value={onboardForm.company} onChange={e=>setOnboardForm(f=>({...f,company:e.target.value}))} placeholder="Company name" />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Phone</label>
                  <input style={INP} value={onboardForm.phone} onChange={e=>setOnboardForm(f=>({...f,phone:e.target.value}))} placeholder="+91 98765 43210" />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Client Type *</label>
                  <select style={INP} value={onboardForm.clientType} onChange={e=>setOnboardForm(f=>({...f,clientType:e.target.value}))} required>
                    <option value="">— Select type —</option>
                    {CLIENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Transaction Currency</label>
                  <select style={INP} value={onboardForm.transactionCurrency} onChange={e=>setOnboardForm(f=>({...f,transactionCurrency:e.target.value}))}>
                    {CURRENCIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', fontSize:12, color:'#15803d' }}>
                ✅ After onboarding, assign projects to this client so they can view them in their portal.
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
                <button type="button" onClick={() => setOnboardModal(false)} style={{ padding:'10px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:14 }}>Cancel</button>
                <button type="submit" disabled={onboardSaving} style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14, opacity: onboardSaving ? 0.7 : 1 }}>
                  {onboardSaving ? 'Creating...' : 'Onboard Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // ── RENDER: detail view ─────────────────────────────────────────────────────
  const s = selected;
  return (
    <div style={{ padding:'28px 32px', background:'#f8fafc', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
        <button onClick={()=>setSelected(null)} style={{ padding:'8px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:14, color:'#475569' }}>
          <FiArrowLeft size={14}/> Back
        </button>
        <div style={{ flex:1 }}>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#1e293b' }}>{s.name}</h1>
          <p style={{ margin:'2px 0 0', color:'#64748b', fontSize:13 }}>{s.email}</p>
        </div>
        {isAdmin && (
          <button onClick={openEditClient} style={{ padding:'8px 16px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:14, color:'#475569' }}>
            <FiEdit2 size={13}/> Edit
          </button>
        )}
      </div>

      {loadingDetail ? (
        <div style={{ textAlign:'center', padding:64, color:'#94a3b8' }}>Loading…</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:20, alignItems:'start' }}>
          {/* Sidebar */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ background:'#fff', borderRadius:16, padding:20, boxShadow:'0 1px 4px #0000000d' }}>
              <div style={{ textAlign:'center', marginBottom:16 }}>
                <div style={{ ...ava(s.name, 64), margin:'0 auto 12px' }}>{(s.name||'C').charAt(0)}</div>
                <div style={{ fontWeight:700, fontSize:17, color:'#1e293b' }}>{s.name}</div>
                <div style={{ fontSize:13, color:'#94a3b8', marginTop:2 }}>{s.email}</div>
                <span style={{ display:'inline-block', marginTop:8, background:s.isActive!==false?'#dcfce7':'#f1f5f9', color:s.isActive!==false?'#14532d':'#94a3b8', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                  {s.isActive!==false?'Active Client':'Inactive'}
                </span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center', marginBottom:12 }}>
                {s.clientType && (
                  <span style={{ background:'#f0f0ff', color:'var(--pm-primary)', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                    {clientTypeIcon(s.clientType)} {clientTypeLabel(s.clientType)}
                  </span>
                )}
                {s.transactionCurrency && (
                  <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                    💱 {s.transactionCurrency}
                  </span>
                )}
              </div>
              {[
                s.company  && { icon:<FiBriefcase size={13}/>, val:s.company },
                s.position && { icon:'💼', val:s.position },
                s.phone    && { icon:<FiPhone size={13}/>, val:s.phone },
              ].filter(Boolean).map((row,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#64748b', marginBottom:8 }}>
                  <span style={{ color:'var(--pm-primary)' }}>{row.icon}</span> {row.val}
                </div>
              ))}
            </div>

            {s.stats && (
              <div style={{ background:'#fff', borderRadius:16, padding:20, boxShadow:'0 1px 4px #0000000d' }}>
                <div style={{ fontWeight:700, color:'#1e293b', marginBottom:14, fontSize:14 }}>Overview</div>
                {[
                  { label:'Total Projects',  val: s.stats.totalProjects||0 },
                  { label:'Active Projects', val: s.stats.activeProjects||0 },
                  { label:'Total Invoices',  val: s.stats.totalInvoices||0 },
                  { label:'Paid Invoices',   val: s.stats.paidInvoices||0 },
                  { label:'Total Revenue',   val: fmt(s.stats.totalRevenue||0) },
                  { label:'Pending Revenue', val: fmt(s.stats.pendingRevenue||0) },
                ].map(row=>(
                  <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f1f5f9' }}>
                    <span style={{ fontSize:13, color:'#64748b' }}>{row.label}</span>
                    <span style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>{row.val}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <div style={{ display:'flex', gap:4, marginBottom:20, background:'#fff', borderRadius:12, padding:6, boxShadow:'0 1px 4px #0000000d', width:'fit-content' }}>
              {[['notes','📝 Notes'],['projects','📁 Projects'],['invoices','🧾 Invoices']].map(([tab,lbl])=>(
                <button key={tab} onClick={()=>setActiveTab(tab)}
                  style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:14, fontWeight:activeTab===tab?700:500, background:activeTab===tab?'var(--pm-primary)':'transparent', color:activeTab===tab?'#fff':'#64748b', transition:'all .2s' }}>
                  {lbl}
                </button>
              ))}
            </div>

            {/* Notes */}
            {activeTab==='notes' && (
              <div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
                  <button onClick={openAddNote} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 }}>
                    <FiPlus size={15}/> Record Communication
                  </button>
                </div>
                {(s.notes||[]).length===0 ? (
                  <div style={{ textAlign:'center', padding:48, color:'#94a3b8', background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d' }}>
                    <div style={{ fontSize:36, marginBottom:10 }}>📝</div>
                    <div style={{ fontWeight:600 }}>No communications recorded yet</div>
                    <div style={{ fontSize:13, marginTop:4 }}>Record calls, WhatsApp messages, requirements, meetings and emails with this client</div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {(s.notes||[]).map(n=>{
                      const nc = NOTE_CFG[n.type]||NOTE_CFG.general;
                      const linkedProj = n.projectId ? (s.projects||[]).find(p=>String(p.id)===String(n.projectId)) : null;
                      const followUpDt = n.followUpDate ? new Date(n.followUpDate) : null;
                      const isOverdue  = followUpDt && followUpDt < new Date() && n.type === 'follow_up';
                      return (
                        <div key={n._id||n.id} style={{ background:'#fff', borderRadius:14, padding:18, boxShadow:'0 1px 4px #0000000d', borderLeft:`4px solid ${n.isPinned?'var(--pm-primary)':nc.text+'44'}` }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              <span style={{ background:nc.bg, color:nc.text, padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>{nc.icon} {nc.label}</span>
                              {n.isPinned && <span style={{ background:'#eef2ff', color:'var(--pm-primary)', padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>📌 Pinned</span>}
                              {linkedProj && <span style={{ background:'#e0f2fe', color:'#0369a1', padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:600 }}>📁 {linkedProj.name}</span>}
                              {followUpDt && (
                                <span style={{ background:isOverdue?'#fee2e2':'#fef3c7', color:isOverdue?'#dc2626':'#92400e', padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                                  {isOverdue?'⚠️':'🔔'} Follow-up: {followUpDt.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                                </span>
                              )}
                            </div>
                            <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                              <button onClick={()=>handlePinNote(n)} title={n.isPinned?'Unpin':'Pin'} style={{ padding:'4px 8px', borderRadius:7, border:'none', background:n.isPinned?'#eef2ff':'#f1f5f9', color:n.isPinned?'var(--pm-primary)':'#64748b', cursor:'pointer' }}><FiMapPin size={13}/></button>
                              <button onClick={()=>openEditNote(n)} style={{ padding:'4px 8px', borderRadius:7, border:'none', background:'#e0e7ff', color:'#4338ca', cursor:'pointer' }}><FiEdit2 size={13}/></button>
                              <button onClick={()=>handleDeleteNote(n._id||n.id)} style={{ padding:'4px 8px', borderRadius:7, border:'none', background:'#fee2e2', color:'#dc2626', cursor:'pointer' }}><FiTrash2 size={13}/></button>
                            </div>
                          </div>
                          {n.title && <div style={{ fontWeight:700, fontSize:15, color:'#1e293b', marginBottom:6 }}>{n.title}</div>}
                          <p style={{ margin:0, fontSize:14, color:'#374151', lineHeight:1.6 }}>{n.content}</p>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10 }}>
                            <div style={ava(n.author?.name||'?', 22)}>{(n.author?.name||'?').charAt(0)}</div>
                            <span style={{ fontSize:12, color:'#94a3b8' }}>
                              {n.author?.name||'Unknown'} · {n.createdAt ? new Date(n.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Projects */}
            {activeTab==='projects' && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {(s.projects||[]).length===0 ? (
                  <div style={{ textAlign:'center', padding:48, color:'#94a3b8', background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d' }}>
                    <div style={{ fontSize:36, marginBottom:10 }}>📁</div>
                    <div style={{ fontWeight:600 }}>No projects assigned to this client</div>
                  </div>
                ) : (s.projects||[]).map(p=>{
                  const sc = PROJ_STATUS[p.status]||PROJ_STATUS.active;
                  return (
                    <div key={p.id} style={{ background:'#fff', borderRadius:14, padding:18, boxShadow:'0 1px 4px #0000000d' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                        <div style={{ fontWeight:700, color:'#1e293b', fontSize:15 }}>{p.name}</div>
                        <span style={{ background:sc.bg, color:sc.text, padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>{sc.label}</span>
                      </div>
                      <div style={{ display:'flex', gap:20, fontSize:13, color:'#64748b', flexWrap:'wrap' }}>
                        {p.budget    && <span>💰 Budget: {fmt(p.budget)}</span>}
                        {p.spent     && <span>💸 Spent: {fmt(p.spent)}</span>}
                        {p.startDate && <span>📅 {new Date(p.startDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>}
                      </div>
                      {p.progress != null && (
                        <div style={{ marginTop:10 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#64748b', marginBottom:4 }}>
                            <span>Progress</span><span>{p.progress||0}%</span>
                          </div>
                          <div style={{ height:6, background:'#f1f5f9', borderRadius:3 }}>
                            <div style={{ height:'100%', borderRadius:3, background:'var(--pm-gradient)', width:`${p.progress||0}%`, transition:'width .4s' }}/>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Invoices */}
            {activeTab==='invoices' && (
              (s.invoices||[]).length===0 ? (
                <div style={{ textAlign:'center', padding:48, color:'#94a3b8', background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d' }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🧾</div>
                  <div style={{ fontWeight:600 }}>No invoices for this client</div>
                </div>
              ) : (
                <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d', overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:'#f8fafc', borderBottom:'2px solid #f1f5f9' }}>
                        {['Invoice #','Issue Date','Due Date','Amount','Status'].map(h=>(
                          <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(s.invoices||[]).map(inv=>(
                        <tr key={inv.id} style={{ borderBottom:'1px solid #f1f5f9' }}
                          onMouseEnter={ev=>ev.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={ev=>ev.currentTarget.style.background='#fff'}>
                          <td style={{ padding:'12px 16px', fontWeight:700, color:'var(--pm-primary)' }}>{inv.invoiceNumber}</td>
                          <td style={{ padding:'12px 16px', fontSize:13, color:'#64748b' }}>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '-'}</td>
                          <td style={{ padding:'12px 16px', fontSize:13, color:'#64748b' }}>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '-'}</td>
                          <td style={{ padding:'12px 16px', fontWeight:700, color:'#1e293b' }}>{fmt(inv.amountInINR||inv.total)}</td>
                          <td style={{ padding:'12px 16px' }}>
                            <span style={{ background:(INV_COLOR[inv.status]||'#94a3b8')+'22', color:INV_COLOR[inv.status]||'#94a3b8', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                              {(inv.status||'').toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Note Modal */}
      {noteModal && (
        <div style={{ position:'fixed', inset:0, background:'#0006', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:'28px 32px', width:'100%', maxWidth:520, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 25px 60px #0000003a' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'#1e293b', fontSize:18 }}>{editNoteId ? 'Edit Communication' : 'Record Communication'}</h3>
              <button onClick={()=>setNoteModal(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>
            <form onSubmit={handleSaveNote} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Channel / Type</label>
                  <select value={noteForm.type} onChange={e=>setNoteForm(f=>({...f,type:e.target.value}))} style={{ ...INP, cursor:'pointer' }}>
                    {NOTE_TYPES.map(t=><option key={t} value={t}>{NOTE_CFG[t]?.icon} {NOTE_CFG[t]?.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Follow-up Date</label>
                  <input type="date" value={noteForm.followUpDate} onChange={e=>setNoteForm(f=>({...f,followUpDate:e.target.value}))} style={INP}/>
                </div>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Subject / Title</label>
                <input value={noteForm.title} onChange={e=>setNoteForm(f=>({...f,title:e.target.value}))} style={INP} placeholder="e.g. New feature request — dashboard redesign"/>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Details *</label>
                <textarea value={noteForm.content} onChange={e=>setNoteForm(f=>({...f,content:e.target.value}))} required style={{ ...INP, minHeight:110, resize:'vertical' }} placeholder="What did the client say or send? Record all details here…"/>
              </div>
              {(s.projects||[]).length > 0 && (
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Related Project <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
                  <select value={noteForm.projectId} onChange={e=>setNoteForm(f=>({...f,projectId:e.target.value}))} style={{ ...INP, cursor:'pointer' }}>
                    <option value="">— Not linked to a project —</option>
                    {(s.projects||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#475569', fontWeight:600 }}>
                <input type="checkbox" checked={!!noteForm.isPinned} onChange={e=>setNoteForm(f=>({...f,isPinned:e.target.checked}))} style={{ width:16, height:16 }}/>
                📌 Pin this note to the top
              </label>
              <div style={{ display:'flex', gap:12, marginTop:6 }}>
                <button type="button" onClick={()=>setNoteModal(false)} style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#64748b', fontWeight:600 }}>Cancel</button>
                <button type="submit" disabled={savingNote} style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>
                  {savingNote ? 'Saving…' : (editNoteId ? 'Update' : 'Record')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editModal && (
        <div style={{ position:'fixed', inset:0, background:'#0006', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:'28px 32px', width:440, boxShadow:'0 25px 60px #0000003a' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'#1e293b', fontSize:18 }}>Edit Client</h3>
              <button onClick={()=>setEditModal(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>
            <form onSubmit={handleSaveClient} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { key:'name',     label:'Name',     placeholder:'Full name' },
                { key:'phone',    label:'Phone',    placeholder:'+91 98765 43210' },
                { key:'company',  label:'Company',  placeholder:'Company name' },
                { key:'position', label:'Position', placeholder:'Designation' },
              ].map(f=>(
                <div key={f.key}>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>{f.label}</label>
                  <input value={clientForm[f.key]||''} onChange={e=>setClientForm(cf=>({...cf,[f.key]:e.target.value}))} style={INP} placeholder={f.placeholder}/>
                </div>
              ))}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Client Type</label>
                  <select value={clientForm.clientType||''} onChange={e=>setClientForm(cf=>({...cf,clientType:e.target.value}))} style={INP}>
                    <option value="">— Select type —</option>
                    {CLIENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Transaction Currency</label>
                  <select value={clientForm.transactionCurrency||'USD'} onChange={e=>setClientForm(cf=>({...cf,transactionCurrency:e.target.value}))} style={INP}>
                    {CURRENCIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#475569', fontWeight:600 }}>
                <input type="checkbox" checked={!!clientForm.isActive} onChange={e=>setClientForm(cf=>({...cf,isActive:e.target.checked}))} style={{ width:16, height:16 }}/>
                Active client
              </label>
              <div style={{ display:'flex', gap:12, marginTop:6 }}>
                <button type="button" onClick={()=>setEditModal(false)} style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#64748b', fontWeight:600 }}>Cancel</button>
                <button type="submit" disabled={savingClient} style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>
                  {savingClient ? 'Saving…' : 'Update Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
